import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal, toggleSpinner } from 'apps/auth/actions/appAction';
import { TableContainer } from 'apps/auth/components/common/components/TableRow/styles';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import { REQUEST_SEND_CLICKED, REQUEST_SHOWN } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { AUTH_REQUEST_PAY_LINK } from 'apps/auth/constants/UrlConstants';
import { canPayBill, getColorForBill, isStatusFailedForBill } from 'apps/auth/helpers/historyUtils';
import { resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';
import { GTRestrictionService } from 'apps/auth/services/GTRestrictionService';

import CheckTableRow from '../send/cheque/units/CheckTableRow';

const RequestDetails = (props) => {
    const { countryId } = useAuth();
    const messages = usePageMessages();
    const { lang } = useLanguage();
    const gtRestrictionService = useRef(null);
    const popupRef = useRef(null);
    const extra = useExtra();
    const cheque = props.cheque.cheque;

    useEffect(() => {
        amplitude.getInstance().logEvent(REQUEST_SHOWN);
        gtRestrictionService.current = new GTRestrictionService(messages, popupRef);
        gtRestrictionService.current.setActions(props.toggleModal, props.toggleSpinner);
        gtRestrictionService.current.setExtra(extra);
        gtRestrictionService.current.setCheque(props.cheque);
    }, []);

    const getAvatar = (chequeForAvatar) => {
        return chequeForAvatar.avatar ? chequeForAvatar.avatar : null;
    };

    const getLetter = (chequeForAvatar) => {
        if (chequeForAvatar.avatar) {
            return null;
        } else if (chequeForAvatar.firstLetter) {
            return chequeForAvatar.firstLetter;
        } else {
            return ':)';
        }
    };

    const getTitle = (chequeForTitle) => {
        if (chequeForTitle.incoming) {
            return resolveArguments(props.chequeIncome, chequeForTitle.billIssuer);
        } else {
            return resolveArguments(props.chequeOutgoing, chequeForTitle.billIssuer);
        }
    };

    const getAmount = (chequeForAmount) => {
        const currency = chequeForAmount.currencyCode && typeof chequeForAmount.currencyCode === 'string' ? chequeForAmount.currencyCode.toLowerCase() : '';
        return (
            <Text size="header" color={getColorForBill(cheque.status)} ellipsis lineThrough={isStatusFailedForBill(cheque.status)}>
                {`${chequeForAmount.formatedAmount} `}
                <Text size="header" smallCaps color={getColorForBill(cheque.status)}>
                    {currency}
                </Text>
            </Text>
        );
    };

    const getStatusIcon = (chequeForIcon) => {
        if (chequeForIcon.status === 'expired') {
            return 'alert';
        } else if (chequeForIcon.status === 'payed') {
            return 'check-rounded';
        } else if (chequeForIcon.status === 'waiting' || chequeForIcon.status === 'created') {
            return 'clock';
        } else if (chequeForIcon.status === 'unpayed' || chequeForIcon.status === 'canceled') {
            return 'cross-rounded';
        }
    };

    const repeatClickHandler = (e) => {
        gtRestrictionService.current.checkChequeRestriction(e, true);
    };

    const getActionButton = (chequeForButtons) => {
        return (
            <Panel
                type="link"
                isHoverable
                negativeMargin
                id={`pay_bill_${chequeForButtons.id}`}
                href={`${AUTH_REQUEST_PAY_LINK}/${chequeForButtons.id}`}
                onClick={(e) => {
                    amplitude.getInstance().logEvent(REQUEST_SEND_CLICKED);
                    repeatClickHandler(e);
                }}
            >
                <DiskBadgeWrapper>
                    <DiskBadge variant="secondary" iconName="check" />
                </DiskBadgeWrapper>
                <Text color="primary">{props.sendButton}</Text>
            </Panel>
        );
    };

    return (
        <>
            <Row indent="adaptive-16-24">
                <DiskBadge
                    src={getAvatar(cheque)}
                    diskText={getLetter(cheque)}
                    diskSize="large"
                    isDisabled={isStatusFailedForBill(cheque.status)}
                    flagName={toLowerCase(props.country.code)}
                />
            </Row>
            <Text variant="div" color={isStatusFailedForBill(cheque.status) ? 'lightGray' : 'primary'} indent="4px" ellipsis>
                {getTitle(cheque)}
            </Text>
            <Row indent="64px">
                <Row indent={!canPayBill(cheque) ? 'adaptive-32-48' : null}>
                    <Row indent="adaptive-8-16">{getAmount(cheque)}</Row>
                    {cheque.comment && (
                        <Text variant="div" color={isStatusFailedForBill(cheque.status) ? 'lightGray' : 'secondary'}>
                            {cheque.comment}
                        </Text>
                    )}
                </Row>
                {!canPayBill(cheque) && (
                    <Row flex align="center">
                        <Icon name={getStatusIcon(cheque)} rightIndent="16px" />
                        {cheque.incoming
                            ? resolveArguments(props.messages[`chequeStatusIn_${cheque.status}`], cheque.billIssuer)
                            : resolveArguments(props.messages[`chequeStatusOut_${cheque.status}`], cheque.billIssuer)}
                    </Row>
                )}
            </Row>
            <>
                {canPayBill(cheque) && (
                    <>
                        <Separator marginTop="0" marginBottom="16px" />
                        {getActionButton(cheque)}
                    </>
                )}
                <Separator marginTop={canPayBill(cheque) ? '16px' : '0'} />
                <TableContainer>
                    <CheckTableRow title={props.chequeExpiryDate} cheque={cheque}>
                        {props.getFormattedChequeDate(countryId, cheque.expireDate, lang)}
                    </CheckTableRow>
                    <CheckTableRow title={props.chequeRequestDate} cheque={cheque}>
                        {props.getFormattedChequeDate(countryId, cheque.createDate, lang)}
                    </CheckTableRow>
                </TableContainer>
                <Separator />
                <TableContainer>
                    <CheckTableRow title={props.chequeOperationNumber} cheque={cheque}>
                        {cheque.id}
                    </CheckTableRow>
                </TableContainer>
            </>
            <PopupNew ref={popupRef} getPopupComponent={(component) => component} />
        </>
    );
};

const mapStateToProps = (state) => ({
    cheque: state.history.cheque,
    country: state.history.cheque.country,
    messages: state.messages.pageMessages,
    chequeIncome: state.messages.pageMessages.chequeIncome,
    chequeOutgoing: state.messages.pageMessages.chequeOutgoing,
    sendButton: state.messages.pageMessages.sendButton,
    chequeExpiryDate: state.messages.pageMessages.chequeExpiryDate,
    chequeRequestDate: state.messages.pageMessages.chequeRequestDate,
    chequeOperationNumber: state.messages.pageMessages.chequeOperationNumber,
});

export default connect(mapStateToProps, { toggleModal, toggleSpinner })(RequestDetails);
